import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import AgencyShell from "@/components/agency/AgencyShell";
import { ReadinessBadge, SeverityBadge, DocStatusBadge } from "@/components/StatusBadge";
import PreparePanel from "@/components/app/prepare/PreparePanel";
import api, { apiErrorMessage, API_BASE, tokenParam } from "@/lib/api";
import { toast } from "sonner";

export function AgencyCase() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState("Overview");

  const load = useCallback(() => {
    api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/agency/team").then((r) => setTeam(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <AgencyShell title="Case"><p className="text-muted">Case unavailable.</p></AgencyShell>;
  if (!data) return <AgencyShell title="Case"><div className="h-40 animate-pulse rounded-md bg-white" /></AgencyShell>;

  const { case: c, corridor, checklist, findings, profile, documents } = data;

  const assign = async (uid) => {
    try { await api.post(`/agency/cases/${id}/assign`, { assigned_to: uid || null }); toast.success("Assignment updated"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <AgencyShell title={`${profile?.full_name} · ${corridor?.destination}`} actions={<ReadinessBadge state={c.readiness_state} size="lg" />}>
      <div className="mb-6 flex gap-6 border-b border-hborder">
        {["Overview", "Prepare file"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`agency-tab-${t === "Overview" ? "overview" : "prepare"}`}
            className={`-mb-px pb-2 text-sm ${tab === t ? "border-b-2 border-navy font-semibold text-navy" : "text-muted hover:text-navy"}`}>{t}</button>
        ))}
      </div>
      {tab === "Prepare file" && <PreparePanel caseId={id} visa={c.visa_category} process={data.process} onChanged={load} />}
      <div className={`grid gap-6 lg:grid-cols-3 ${tab === "Prepare file" ? "hidden" : ""}`}>
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Requirements</h2>
            <div className="mt-3 space-y-2">
              {checklist.map((r) => (
                <div key={r.code} className="flex items-center justify-between border-b border-hborder/50 py-2 last:border-0">
                  <span className="text-sm text-navy">{r.title}{r.required && <span className="ml-1 text-xs text-coral">*</span>}</span>
                  {r.satisfied ? <DocStatusBadge status="Reviewed" /> : <span className="text-xs text-muted">missing</span>}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Documents</h2>
            <div className="mt-3 space-y-2">
              {documents.length === 0 ? <p className="text-sm text-muted">No documents.</p> : documents.map((d) => (
                <a key={d.id} href={`${API_BASE}/documents/${d.id}/file${tokenParam()}`} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-lg border border-hborder p-3 hover:bg-softcream" data-testid={`agency-doc-${d.id}`}>
                  <span className="flex items-center gap-2 text-sm text-navy"><FileText size={15} /> {d.original_filename}</span>
                  <DocStatusBadge status={d.status} />
                </a>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Findings</h2>
            <div className="mt-3 space-y-3">
              {findings.length === 0 ? <p className="text-sm text-muted">No findings.</p> : findings.map((f) => (
                <div key={f.id} className="rounded-lg border border-hborder p-3">
                  <div className="flex items-center gap-2"><SeverityBadge severity={f.severity} />{f.status !== "open" && <span className="text-xs text-emerald-700">{f.status}</span>}</div>
                  <p className="mt-1.5 font-medium text-heading">{f.title}</p>
                  <p className="text-sm text-muted">{f.explanation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Assignment</h2>
            <select value={c.assigned_to || ""} onChange={(e) => assign(e.target.value)} className="mt-3 w-full rounded-lg border border-hborder px-3 py-2 text-sm text-navy" data-testid="assign-select">
              <option value="">Unassigned</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="rounded-md border border-hborder bg-white p-5 text-sm">
            <h2 className="font-semibold text-heading">Case details</h2>
            <p className="mt-2 text-muted">Status: <span className="text-navy">{c.status}</span></p>
            <p className="text-muted">Review: <span className="text-navy">{c.review_state || "not requested"}</span></p>
            <p className="text-muted">Target travel: <span className="text-navy">{c.target_travel_date || "Not set"}</span></p>
          </div>
        </div>
      </div>
    </AgencyShell>
  );
}
